import { Close, EditOutlined } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import {
    CardHeaderAction,
    DetailsCard,
    DetailsCardHeader,
} from '~/components/DetailsCard';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import ModalityChip from '~/components/Chip/ModalityChip';
import AxisTripletValue, {
    type AxisValue,
} from '~/components/Fields/AxisTripletValue';
import {
    LabelText,
    labelValueGridSx,
    ValueText,
} from '~/components/Fields/LabeledValue';
import NumberValue from '~/components/Fields/NumberValue';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { ApprovalStatusLabel } from '~/components/Structures/ApprovalMark';
import { menuProps } from '~/components/toolbar/OperationsToolbar/selectMenuProps';
import { useActiveFusionTarget } from '~/hooks/target/useActiveFusionTarget';
import { useActiveFusionWriter } from '~/hooks/target/useActiveFusionWriter';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { Modality } from '~/domain/dicom/modality';
import {
    domainEventBus,
    RegistrationApprovalStatusChangeRequestedEvent,
    RegistrationCreateRequestedEvent,
    RegistrationDeleteRequestedEvent,
    RegistrationRenameRequestedEvent,
} from '~/domain/events';
import { Image3D } from '~/domain/patient/image3d';
import { RegistrationView } from '~/domain/projections/views';
import { reportImageDownloadFailure } from '~/hooks/image/reportImageDownloadFailure';
import useLoadAvailableImage from '~/hooks/image/useLoadAvailableImage';
import { AvailableImage } from '~/hooks/image/usePrimaryImage';
import useBusyIndicator from '~/hooks/useBusyIndicator';
import {
    useActivateRegistration,
    useRegistrationState,
} from '~/hooks/registration/useRegistrationStore';
import { autoRegistrationInProgressState } from '~/state/atoms/toolsAtoms';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { formatAngle } from '~/utils/obliquity';
import { type AxisKey, formatMmAsCm } from '~/utils/units';
import { formatDateString } from '~/utils/date';
import { decomposeRigidTransform } from '~/domain/patient/rigidTransformComponents';
import RegistrationHeader from './RegistrationHeader';
import RegistrationMenu from './RegistrationMenu';

interface RegistrationPanelProps {
    image: AvailableImage;
    primaryImage: Image3D;
    isPrimary?: boolean;
    onClose: () => void;
}

export default function RegistrationPanel({
    image,
    primaryImage,
    isPrimary = false,
    onClose,
}: RegistrationPanelProps) {
    const activateRegistration = useActivateRegistration();
    const { clear: clearFusion } = useActiveFusionWriter();
    const registrationState = useRegistrationState();
    const intl = useIntl();
    const loadAvailableImage = useLoadAvailableImage();
    const { showBusy, hideBusy } = useBusyIndicator();
    const isAutoRegistering = useRecoilValue(autoRegistrationInProgressState);

    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);
    const [isRenaming, setIsRenaming] = useState(false);
    // The editor shows the label in clear, so hiding patient info closes it.
    useEffect(() => {
        if (!showSensitiveInfo) {
            setIsRenaming(false);
        }
    }, [showSensitiveInfo]);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);

    const availableRegistrations = useMemo(() => {
        const imageReg = registrationState.imageRegistrations.find(
            (ir) => ir.imageSeriesUid === image.seriesInstanceUID
        );
        return imageReg?.registrations ?? [];
    }, [registrationState.imageRegistrations, image.seriesInstanceUID]);

    const activeRegistration = useMemo(() => {
        if (!registrationState.selectedSopInstanceUid) {
            return null;
        }
        const found = availableRegistrations.find(
            (r) => r.sopInstanceUid === registrationState.selectedSopInstanceUid
        );
        return found ?? null;
    }, [availableRegistrations, registrationState.selectedSopInstanceUid]);

    const isApproved = activeRegistration?.isApproved ?? false;

    const sharedFrameOfReference = useMemo(() => {
        if (!image.imageObject) {
            return false;
        }
        return (
            primaryImage.frameOfReferenceUid ===
            image.imageObject.frameOfReferenceUid
        );
    }, [primaryImage, image]);

    // Don't show panel for primary images
    if (isPrimary) {
        return null;
    }

    const handleCreate = async () => {
        // If the image hasn't been DICOM-loaded yet (e.g. a
        // session secondary image), download and process it
        // first so the registration store can resolve it.
        if (!image.isLoaded) {
            setIsDownloading(true);
            await showBusy(intl.formatMessage({ id: 'downloadingImage' }));
            try {
                if (!(await loadAvailableImage(image))) {
                    reportImageDownloadFailure(intl);
                    return;
                }
            } catch (error) {
                handleError(error);
                reportImageDownloadFailure(intl, error);
                return;
            } finally {
                setIsDownloading(false);
                hideBusy();
            }
        }

        domainEventBus.publish(
            new RegistrationCreateRequestedEvent(
                primaryImage.seriesUid,
                image.seriesInstanceUID
            )
        );
    };

    const handleRename = (newLabel: string) => {
        if (activeRegistration) {
            domainEventBus.publish(
                new RegistrationRenameRequestedEvent(
                    activeRegistration.sopInstanceUid,
                    newLabel
                )
            );
        }
    };

    const handleDelete = () => {
        if (activeRegistration && !isApproved) {
            domainEventBus.publish(
                new RegistrationDeleteRequestedEvent(
                    activeRegistration.sopInstanceUid
                )
            );
            clearFusion(primaryImage);
        }
        setIsDeleteDialogOpen(false);
    };

    const headerActions = activeRegistration ? (
        <RegistrationMenu
            cardRef={cardRef}
            isApproved={isApproved}
            isDisabled={isAutoRegistering}
            hasSharedFrameOfReference={sharedFrameOfReference}
            availableRegistrations={availableRegistrations}
            activeRegistrationSopUid={activeRegistration.sopInstanceUid}
            onCreateNew={handleCreate}
            onDelete={() => setIsDeleteDialogOpen(true)}
            onChangeRegistration={(sopUid) =>
                activateRegistration(sopUid, image.seriesInstanceUID)
            }
        />
    ) : null;

    return (
        <>
            <DetailsCard ref={cardRef} data-testid="registration-details-card">
                <DetailsCardHeader>
                    <Box flex={1} minWidth={0} mr={0.5}>
                        <RegistrationHeader
                            activeRegistration={activeRegistration}
                            onRename={handleRename}
                            isRenaming={isRenaming}
                            onStartRename={() => setIsRenaming(true)}
                            onCancelRename={() => setIsRenaming(false)}
                        />
                    </Box>
                    {activeRegistration && (
                        <CardHeaderAction
                            Icon={EditOutlined}
                            label={intl.formatMessage({ id: 'rename' })}
                            onClick={() => setIsRenaming(true)}
                            disabled={
                                isApproved ||
                                isAutoRegistering ||
                                !showSensitiveInfo
                            }
                            disabledTooltip={
                                isApproved ? (
                                    <FormattedMessage id="registration.approved" />
                                ) : !showSensitiveInfo ? (
                                    <FormattedMessage id="registration.patientInfoHidden" />
                                ) : undefined
                            }
                            data-testid="registration-rename-button"
                        />
                    )}
                    {/* More and Close stand flush, as on the structure
                            card. */}
                    <Box display="flex" mx={-0.5}>
                        {headerActions}
                    </Box>
                    <CardHeaderAction
                        Icon={Close}
                        label={intl.formatMessage({ id: 'close' })}
                        onClick={onClose}
                        data-testid="close-bottom-panel"
                    />
                </DetailsCardHeader>
                {!activeRegistration ? (
                    <Stack spacing={1}>
                        <Button
                            data-testid="create-registration-button"
                            variant="contained"
                            startIcon={
                                isDownloading ? (
                                    <CircularProgress size={14} />
                                ) : (
                                    <Plus size={14} />
                                )
                            }
                            onClick={handleCreate}
                            disabled={
                                sharedFrameOfReference ||
                                isDownloading ||
                                isAutoRegistering
                            }
                            fullWidth
                        >
                            <FormattedMessage
                                id={
                                    isDownloading
                                        ? 'downloadingImage'
                                        : 'registrationNew'
                                }
                            />
                        </Button>
                        {sharedFrameOfReference && (
                            <Typography
                                variant="body2"
                                fontSize={12}
                                color="text.secondary"
                            >
                                <FormattedMessage id="registration.sharedForNoRegistration" />
                            </Typography>
                        )}
                    </Stack>
                ) : (
                    <RegistrationDetails
                        primaryImage={primaryImage}
                        image={image}
                        registration={activeRegistration}
                    />
                )}
            </DetailsCard>

            <ConfirmationDialog
                open={isDeleteDialogOpen}
                title={<FormattedMessage id="deleteRegistration" />}
                message={<FormattedMessage id="deleteRegistrationConfirm" />}
                onConfirm={handleDelete}
                onCancel={() => setIsDeleteDialogOpen(false)}
                confirmText={<FormattedMessage id="delete" />}
                cancelText={<FormattedMessage id="cancel" />}
                mode="error"
            />
        </>
    );
}

const AXES: readonly AxisKey[] = ['x', 'y', 'z'];

/** The x, y and z values of a transform, each with its axis. */
function axesOf(values: readonly string[]): AxisValue[] {
    return AXES.map((axis, index) => ({ axis, value: values[index] }));
}

/**
 * An image of the pair: its tag chip, then its name, which is PHI. The chip
 * stands in a box one text line tall, so its row is as tall as the others.
 */
function ImageValue({ modality, name }: { modality: Modality; name: string }) {
    return (
        <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
            <Box
                display="flex"
                alignItems="center"
                flexShrink={0}
                fontSize={12}
                height="1.2em"
            >
                <ModalityChip modality={modality} size="small" />
            </Box>
            <ValueText>
                <SensitiveInfoTypography>{name}</SensitiveInfoTypography>
            </ValueText>
        </Box>
    );
}

interface RegistrationDetailsProps {
    primaryImage: Image3D;
    image: AvailableImage;
    registration: RegistrationView;
}

function RegistrationDetails({
    primaryImage,
    image,
    registration,
}: RegistrationDetailsProps) {
    const intl = useIntl();
    // The pair's live Environment re-transforms on every matrix write and
    // announces it; the readout follows that, on whichever surface holds
    // the pair.
    const { environment, displayedRegistration: registrationObject } =
        useActiveFusionTarget();

    const [pendingStatus, setPendingStatus] =
        useState<DICOMApprovalStatus | null>(null);

    const primary = useMemo(
        () => ({
            modality: primaryImage.firstSlice.modality,
            name: primaryImage.seriesMetadata.seriesDescription || 'Primary',
        }),
        [primaryImage]
    );

    const secondary = useMemo(
        () => ({
            modality: image.modality,
            name:
                image.imageObject?.seriesMetadata.seriesDescription ||
                image.label ||
                'Secondary',
        }),
        [image]
    );

    // Determine source/target from the actual registration FOR UIDs
    // rather than assuming primary is always source
    const primaryIsSource =
        registration.sourceFrameOfReferenceUid ===
        primaryImage.frameOfReferenceUid;
    const source = primaryIsSource ? primary : secondary;
    const target = primaryIsSource ? secondary : primary;

    const sharedFrameOfReference = useMemo(() => {
        if (!image.imageObject) {
            return false;
        }
        return (
            primaryImage.frameOfReferenceUid ===
            image.imageObject.frameOfReferenceUid
        );
    }, [primaryImage, image]);

    const computeTransform = useCallback(() => {
        if (!registrationObject) {
            return null;
        }
        return decomposeRigidTransform(registrationObject.transformation);
    }, [registrationObject]);

    const [transformData, setTransformData] = useState(computeTransform);

    useEffect(() => {
        if (!environment) {
            return;
        }
        const handler = () => setTransformData(computeTransform());
        environment.eventTarget.addEventListener(
            'registration_changed',
            handler
        );
        return () => {
            environment.eventTarget.removeEventListener(
                'registration_changed',
                handler
            );
        };
    }, [environment, computeTransform]);

    useEffect(() => {
        setTransformData(computeTransform());
    }, [computeTransform]);

    const handleApprovalStatusChange = (
        e: SelectChangeEvent<DICOMApprovalStatus>
    ) => {
        const newStatus = e.target.value as DICOMApprovalStatus;
        if (newStatus !== registration.approvalStatus) {
            setPendingStatus(newStatus);
        }
    };

    const confirmApprovalStatusChange = () => {
        if (pendingStatus) {
            domainEventBus.publish(
                new RegistrationApprovalStatusChangeRequestedEvent(
                    registration.sopInstanceUid,
                    pendingStatus
                )
            );
        }
        setPendingStatus(null);
    };

    const allStatuses = Object.values(DICOMApprovalStatus);
    const currentStatus = allStatuses.includes(registration.approvalStatus)
        ? registration.approvalStatus
        : DICOMApprovalStatus.UNAPPROVED;

    return (
        <Box display="flex" flexDirection="column" gap={0.5}>
            {registration.contentDescription?.length > 0 && (
                <Typography variant="body2" fontSize={13} sx={{ pb: 0.5 }}>
                    <SensitiveInfoTypography>
                        {registration.contentDescription}
                    </SensitiveInfoTypography>
                </Typography>
            )}

            <Box sx={{ ...labelValueGridSx, gap: '6px 8px' }}>
                <LabelText>
                    <FormattedMessage id="registration.sourceImage" />
                </LabelText>
                <ImageValue {...source} />
                <LabelText>
                    <FormattedMessage id="registration.targetImage" />
                </LabelText>
                <ImageValue {...target} />
                {sharedFrameOfReference && (
                    <>
                        <LabelText>
                            <FormattedMessage id="frameOfReference" />
                        </LabelText>
                        <ValueText>
                            <FormattedMessage id="registration.sharedFor" />
                        </ValueText>
                    </>
                )}
                <LabelText>
                    <FormattedMessage id="registration.contentCreator" />
                </LabelText>
                <ValueText>
                    {registration.contentCreatorName ? (
                        <SensitiveInfoTypography>
                            {registration.contentCreatorName}
                        </SensitiveInfoTypography>
                    ) : (
                        <FormattedMessage id="unknown" />
                    )}
                </ValueText>
                {registration.contentDate && (
                    <>
                        <LabelText>
                            <FormattedMessage id="registration.contentDate" />
                        </LabelText>
                        <NumberValue
                            value={
                                <SensitiveInfoTypography>
                                    {formatDateString(
                                        registration.contentDate
                                    ) || registration.contentDate}
                                </SensitiveInfoTypography>
                            }
                        />
                    </>
                )}
                {transformData && (
                    <>
                        <LabelText>
                            <FormattedMessage id="registration.translation" />
                        </LabelText>
                        <AxisTripletValue
                            axes={axesOf(
                                transformData.translationMm.map((mm) =>
                                    formatMmAsCm(mm)
                                )
                            )}
                            unit="cm"
                            wrap
                            data-testid="registration-translation-value"
                        />
                        <Tooltip
                            describeChild
                            title={intl.formatMessage({
                                id: 'registration.eulerAngles.tooltip',
                            })}
                        >
                            <LabelText>
                                <FormattedMessage id="registration.eulerAngles" />
                            </LabelText>
                        </Tooltip>
                        <AxisTripletValue
                            axes={axesOf(
                                transformData.eulerAnglesDeg.map((angle) =>
                                    formatAngle(angle)
                                )
                            )}
                            unit="°"
                            wrap
                            data-testid="registration-rotation-value"
                        />
                    </>
                )}
            </Box>
            <Box display="flex" flexDirection="column" gap={0.5} mt={0.5}>
                <LabelText id="registration-approval-status-label">
                    <FormattedMessage id="approvalStatus" />
                </LabelText>
                <Select
                    size="small"
                    fullWidth
                    labelId="registration-approval-status-label"
                    value={currentStatus}
                    onChange={handleApprovalStatusChange}
                    MenuProps={menuProps}
                    renderValue={(selected) => (
                        <ApprovalStatusLabel status={selected} size={14} />
                    )}
                >
                    {/* The current status stays in the list: MUI warns
                        (out-of-range value) when the Select's value has
                        no matching option. Re-selecting it is a no-op:
                        the change handler ignores non-transitions. */}
                    {allStatuses.map((status) => (
                        <MenuItem
                            key={status}
                            value={status}
                            sx={{ fontSize: 12 }}
                        >
                            <ApprovalStatusLabel status={status} size={14} />
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            <ConfirmationDialog
                open={pendingStatus !== null}
                title={
                    <FormattedMessage id="registration.confirmApprovalStatusChange" />
                }
                message={
                    <FormattedMessage
                        id="registration.approvalStatusChangeDescription"
                        values={{
                            newApprovalStatus: pendingStatus
                                ? intl.formatMessage({
                                      id:
                                          pendingStatus ===
                                          DICOMApprovalStatus.APPROVED
                                              ? 'approved'
                                              : pendingStatus ===
                                                  DICOMApprovalStatus.REJECTED
                                                ? 'rejected'
                                                : 'unapproved',
                                  })
                                : '',
                        }}
                    />
                }
                onConfirm={confirmApprovalStatusChange}
                onCancel={() => setPendingStatus(null)}
                confirmText={<FormattedMessage id="confirm" />}
                cancelText={<FormattedMessage id="cancel" />}
                mode="primary"
            />
        </Box>
    );
}
